import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IFirm } from 'app/models/firms/firm';
import { IFirmLeadCredential } from 'app/models/firms/firmLeadCredentials';
import { FirmService } from 'app/services/firm/firm-service';
import { ToastrService } from 'ngx-toastr';
import { AddEditFirmStaffComponent } from '../add-edit-firm-staff/add-edit-firm-staff.component';
import { FIRM_CREDENTIALS, FIRM_CREDENTIAL_MAPPING, LEAD_BASED_PAINT } from '@shared/utils/app-static-data';

@Component({
  selector: 'app-add-edit-firm-credential',
  templateUrl: './add-edit-firm-credential.component.html',
  styleUrls: ['./add-edit-firm-credential.component.scss']
})
export class AddEditFirmCredentialComponent implements OnInit {
  public firmCredentials: string[] = Object.values(FIRM_CREDENTIALS);
  public selectedCredential: string = "";
  public credentialToAdd: IFirmLeadCredential = {id:0, firmLeadCredentialType:"", firm: null};
  public uiData = LEAD_BASED_PAINT;
  public credentialMapping = FIRM_CREDENTIAL_MAPPING;  
  public firmValidatedCredential: {isValid: boolean, staffIds:any[], totalStaff: number, expirationDate: string} = {isValid: false, staffIds: [], totalStaff: 0, expirationDate: ""};
  
  constructor(
    private readonly dialogRef: MatDialogRef<AddEditFirmStaffComponent>,    
    public firmService: FirmService,
    @Inject(MAT_DIALOG_DATA) public data:{firm:IFirm, credential:IFirmLeadCredential, firmCredentials:IFirmLeadCredential[], staffCerts:any[]},      
    private readonly toastr: ToastrService
  ) 
  { 
    if(this.data.credential !== null){
      this.credentialToAdd = this.data.credential;
      this.selectedCredential = this.data.credential.firmLeadCredentialType;
      this.validateFirmCredentials();
    }
    else{
      this.credentialToAdd.firm = this.data.firm;
    }            
  }

  ngOnInit(): void {
    this.firmCredentials = [this.data?.credential?.firmLeadCredentialType].concat(Object.values(FIRM_CREDENTIALS).filter(credential => !this.data.firmCredentials.some(currentCredential => currentCredential.firmLeadCredentialType === credential)));
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
  onSubmit(): void {
    this.credentialToAdd.firmLeadCredentialType = this.selectedCredential;
    this.firmService.saveFirmLeadCredential(this.credentialToAdd).subscribe(result=>{
      this.toastr.success("Successfully added firm credential");
      this.dialogRef.close(true);
    },error=>{
      this.toastr.error("An error occurred trying to add the firm credential: ", error);
    });
  }

  onCredentialChange(): void {
    this.firmValidatedCredential = {isValid: false, staffIds: [], totalStaff: 0, expirationDate: ""};
    this.validateFirmCredentials();
  }

  validateFirmCredentials(): void {
    let value = this.firmCredentialsMappedToPersonCerts();      
    
    this.firmValidatedCredential = {
      isValid: value.isValid,
      staffIds: value.staffIds,
      totalStaff: value.staffIds.length,
      expirationDate: value.expirationDate
    }    
  }

  firmCredentialsMappedToPersonCerts() {
    const returnObject: { isValid: boolean; staffIds: any[]; expirationDate: string } = { isValid: false, staffIds: [], expirationDate: "" };
    const credentialTypes = (this.credentialMapping as any)[this.selectedCredential] ?? [];
    const now = new Date();

    // Keep one staff record per person using that person's furthest valid certificate.
    const latestByPerson = new Map<any, any>();
    let furthestExpirationTime = 0;
    let furthestExpirationIso = "";

    this.data.staffCerts?.forEach(member => {
      member.certs.forEach((cert: any) => {
        if (!credentialTypes.includes(cert.codeType)) {
          return;
        }

        const expiration = cert.expirationDate ? new Date(cert.expirationDate) : null;
        if (!expiration || isNaN(expiration.getTime()) || expiration < now) {
          return;
        }

        returnObject.isValid = true;

        const personId = cert.person?.id;
        const staffObject = {
          id: personId,
          display: cert.person?.id + ': ' + cert.person?.firstName + ' ' + cert.person?.lastName,
          expiration: cert.expirationDate,
          codeType: cert.codeType
        };

        if (!latestByPerson.has(personId)) {
          latestByPerson.set(personId, staffObject);
        } else {
          const existing = latestByPerson.get(personId);
          const existingDate = existing?.expiration ? new Date(existing.expiration) : null;
          if (!existingDate || expiration > existingDate) {
            latestByPerson.set(personId, staffObject);
          }
        }

        const expirationTime = expiration.getTime();
        if (expirationTime > furthestExpirationTime) {
          furthestExpirationTime = expirationTime;
          furthestExpirationIso = expiration.toISOString();
        }
      });
    });

    returnObject.staffIds = Array.from(latestByPerson.values())
      .sort((a, b) => new Date(b.expiration).getTime() - new Date(a.expiration).getTime());
    returnObject.expirationDate = furthestExpirationIso;

    return returnObject;
  }
}
